/// <reference types="vite/client" />
/**
 * i18next configuration for the business webapp.
 *
 * Language detection priority:
 *   1. User override saved in localStorage (`cravo-lang-override`).
 *      A manual picker in Settings wins over the tenant default so a
 *      bilingual user can stay in English even if the tenant ships Hindi.
 *   2. Tenant default from `window.__TENANT_CONFIG__.branding.localization.language`.
 *      Set by the LabelGenic admin and injected by the Pages middleware.
 *   3. Browser `navigator.language` (e.g., `hi-IN` → `hi`).
 *   4. Hardcoded fallback `en`.
 *
 * Locale resources are lazy-loaded — only the active language's JSON ships
 * to the user. `en.json` is the source of truth; any missing key in another
 * locale falls back to English so no UI ever renders an empty string.
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

export const SUPPORTED_LANGUAGES = ['en', 'hi', 'ar', 'es', 'de', 'tr'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en'

// Right-to-left scripts. Used to toggle `<html dir="rtl">` and Tailwind
// `rtl:` variants so layouts mirror correctly for Arabic.
const RTL_LANGUAGES = new Set<SupportedLanguage>(['ar'])

export const USER_LANG_OVERRIDE_KEY = 'cravo-lang-override'

export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

export function isRtlLanguage(lang: string): boolean {
  return RTL_LANGUAGES.has(lang as SupportedLanguage)
}

function normalizeLangTag(value: string | null | undefined): SupportedLanguage | null {
  if (!value) return null
  const base = value.trim().toLowerCase().split(/[-_]/)[0]
  return isSupportedLanguage(base) ? base : null
}

function readUserOverride(): SupportedLanguage | null {
  if (typeof window === 'undefined') return null
  try {
    return normalizeLangTag(window.localStorage.getItem(USER_LANG_OVERRIDE_KEY))
  } catch {
    return null
  }
}

function readTenantLanguage(): SupportedLanguage | null {
  if (typeof window === 'undefined') return null
  const cfg = (window as unknown as {
    __TENANT_CONFIG__?: { branding?: { localization?: { language?: string } } }
  }).__TENANT_CONFIG__
  return normalizeLangTag(cfg?.branding?.localization?.language)
}

function readBrowserLanguage(): SupportedLanguage | null {
  if (typeof navigator === 'undefined') return null
  return normalizeLangTag(navigator.language)
}

export function resolveInitialLanguage(): SupportedLanguage {
  return (
    readUserOverride() ??
    readTenantLanguage() ??
    readBrowserLanguage() ??
    DEFAULT_LANGUAGE
  )
}

// Locale files are imported via Vite's glob so each one becomes its own
// chunk and only the active language ships at runtime. The `as: 'json'`
// import attribute lets Vite inline the JSON during build.
const localeImporters = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*.json',
)

async function loadLocale(lang: SupportedLanguage): Promise<Record<string, unknown>> {
  const importer = localeImporters[`./locales/${lang}.json`]
  if (!importer) return {}
  const mod = await importer()
  return mod.default
}

let initialized = false

export async function initI18n(): Promise<typeof i18n> {
  if (initialized) return i18n
  initialized = true

  const initialLang = resolveInitialLanguage()
  const initialResources = await loadLocale(initialLang)
  // Always ship the English baseline so missing keys never render blank.
  const fallbackResources = initialLang === 'en' ? initialResources : await loadLocale('en')

  await i18n
    .use(initReactI18next)
    .init({
      lng: initialLang,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
      resources: {
        [initialLang]: { translation: initialResources },
        ...(initialLang !== 'en' ? { en: { translation: fallbackResources } } : {}),
      },
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      returnNull: false,
    })

  applyHtmlLangAttributes(initialLang)
  return i18n
}

export async function changeLanguage(lang: SupportedLanguage, persistOverride = true): Promise<void> {
  if (!i18n.hasResourceBundle(lang, 'translation')) {
    const resources = await loadLocale(lang)
    i18n.addResourceBundle(lang, 'translation', resources, true, true)
  }
  await i18n.changeLanguage(lang)
  applyHtmlLangAttributes(lang)
  if (persistOverride && typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(USER_LANG_OVERRIDE_KEY, lang)
    } catch {
      // Storage may be blocked (incognito, quota); the language still applies
      // for the current session — we just can't persist the preference.
    }
  }
}

export function clearLanguageOverride(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(USER_LANG_OVERRIDE_KEY)
  } catch {
    // Ignore.
  }
}

function applyHtmlLangAttributes(lang: string): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.setAttribute('lang', lang)
  root.setAttribute('dir', isRtlLanguage(lang) ? 'rtl' : 'ltr')
}

export { i18n }
